import React, { useCallback, useEffect, useState } from "react";
import api, { daysAgo, prettyPhone, prettyStamp, ymd } from "../api";
import "../App.css";

const PAGE = 50;

const History = () => {
  const [from, setFrom] = useState(ymd(daysAgo(7)));
  const [to, setTo] = useState(ymd(new Date()));
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ total: 0, rows: [] });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async (p = 0) => {
    setBusy(true);
    setErr("");
    try {
      const res = await api.get("/ui/history", {
        params: { from, to, status, q, offset: p * PAGE, limit: PAGE },
      });
      setData({ total: res.data.total, rows: res.data.rows });
      setPage(p);
    } catch (e) {
      setErr(e.response?.data?.error || "내역을 불러오지 못했습니다.");
    } finally {
      setBusy(false);
    }
  }, [from, to, status, q]);

  useEffect(() => { load(0); /* 처음 한 번 */ // eslint-disable-next-line
  }, []);

  const quick = (days) => {
    setFrom(ymd(daysAgo(days)));
    setTo(ymd(new Date()));
  };

  const csv = () => {
    const head = ["시각", "수신번호", "발신번호", "통신사", "상태", "사유", "내용"];
    const lines = data.rows.map((r) =>
      [prettyStamp(r.reservedAt), r.phone, r.sender, r.telco || "", r.state, r.reason,
       String(r.contents || "").replace(/\r?\n/g, " ")]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")
    );
    // 엑셀이 UTF-8 로 읽도록 BOM 을 붙인다
    const blob = new Blob(["﻿" + [head.join(","), ...lines].join("\r\n")],
      { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `문자내역_${from}_${to}.csv`;
    a.click();
  };

  const pages = Math.ceil(data.total / PAGE);

  return (
    <div className="history">
      <h2 className="title">전송내역</h2>

      <div className="filter card">
        <div className="row">
          <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <span className="tilde">~</span>
          <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          <button className="btn ghost sm" onClick={() => quick(0)}>오늘</button>
          <button className="btn ghost sm" onClick={() => quick(7)}>7일</button>
          <button className="btn ghost sm" onClick={() => quick(30)}>30일</button>
        </div>
        <div className="row">
          <select className="input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">전체</option>
            <option value="success">도착</option>
            <option value="fail">실패</option>
            <option value="pending">대기·처리중</option>
          </select>
          <input
            className="input grow"
            placeholder="수신번호 또는 내용으로 검색"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(0)}
          />
          <button className="btn primary" onClick={() => load(0)} disabled={busy}>
            {busy ? "찾는 중…" : "조회"}
          </button>
          <button className="btn ghost" onClick={csv} disabled={!data.rows.length}>엑셀로 내려받기</button>
        </div>
      </div>

      {err && <div className="notice bad">{err}</div>}

      <div className="count">
        전체 <b>{data.total.toLocaleString()}</b>건
        {pages > 1 && <> · {page + 1}/{pages} 쪽</>}
      </div>

      <table className="tbl wide">
        <thead>
          <tr>
            <th style={{ width: 150 }}>보낸 시각</th>
            <th style={{ width: 120 }}>수신번호</th>
            <th style={{ width: 60 }}>통신사</th>
            <th style={{ width: 80 }}>상태</th>
            <th>내용</th>
            <th style={{ width: 200 }}>실패 사유</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={`${r.src}-${r.uid}`}>
              <td>{prettyStamp(r.reservedAt)}</td>
              <td>{prettyPhone(r.phone)}</td>
              <td>{r.telco === "UNK" ? "-" : r.telco}</td>
              <td>
                <span className={"state " + (r.ok === true ? "ok" : r.ok === false ? "bad" : "wait")}>
                  {r.state}
                </span>
              </td>
              <td className="content"><pre>{r.contents}</pre></td>
              <td className="reason">{r.reason}{r.result && r.result !== "1000" ? ` (${r.result})` : ""}</td>
            </tr>
          ))}
          {!data.rows.length && !busy && (
            <tr><td colSpan={6} className="empty">이 기간에 보낸 문자가 없습니다.</td></tr>
          )}
        </tbody>
      </table>

      {pages > 1 && (
        <div className="row center pager">
          <button className="btn ghost sm" disabled={page === 0} onClick={() => load(page - 1)}>이전</button>
          <span>{page + 1} / {pages}</span>
          <button className="btn ghost sm" disabled={page + 1 >= pages} onClick={() => load(page + 1)}>다음</button>
        </div>
      )}
    </div>
  );
};

export default History;
